import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Copy, ExternalLink } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { FileField } from "./FileField";
import { useAdminT } from "./adminI18n";

const EMPTY = {
  slug: "", title: "", subtitle: "", body: "", image: "", bg_image: "",
  is_offer: false, offer_image: "", offer_title: "", offer_start: "", offer_end: "",
  is_campaign: false, start_badge: "", payment_link_regular: "", payment_link_vip: "",
  cta_text: "سجّل/سجّلي الآن", magnet_file_id: "", is_published: true,
  title_en: "", subtitle_en: "", body_en: "", cta_text_en: "",
  type: "event", course_id: "", payment_link: "",
  layout: "classic", accent_color: "", bg_color: "",
};

const PAGE_TYPES = [
  { value: "event", label: "فعالية" },
  { value: "workshop", label: "ورشة عمل" },
  { value: "retreat", label: "ريتريت" },
  { value: "course", label: "دورة" },
  { value: "general", label: "عام" },
];

const typeLabel = (v) => (PAGE_TYPES.find((t) => t.value === v) || PAGE_TYPES[0]).label;

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm text-luxury-gray mb-2">{label}</label>
    {children}
  </div>
);

export const PagesTab = () => {
  const { t } = useAdminT();
  const [pages, setPages] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/pages`, { headers: authHeaders() }).then((res) => setPages(res.data)).catch(() => {});

  useEffect(() => {
    load();
    axios.get(`${API}/courses`).then((res) => setCourses(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  const startEdit = (p) => {
    setForm({
      slug: p.slug, title: p.title, subtitle: p.subtitle || "", body: p.body || "", image: p.image || "", bg_image: p.bg_image || "",
      is_offer: !!p.is_offer, offer_image: p.offer_image || "", offer_title: p.offer_title || "", offer_start: p.offer_start || "", offer_end: p.offer_end || "",
      is_campaign: !!p.is_campaign, start_badge: p.start_badge || "", payment_link_regular: p.payment_link_regular || (p.is_campaign ? (p.payment_link || "").trim() : "") || "", payment_link_vip: p.payment_link_vip || "",
      cta_text: p.cta_text || "", magnet_file_id: p.magnet_file_id || "", is_published: p.is_published !== false,
      title_en: p.title_en || "", subtitle_en: p.subtitle_en || "", body_en: p.body_en || "", cta_text_en: p.cta_text_en || "",
      type: p.type || "event", course_id: p.course_id || "", payment_link: p.payment_link || "",
      layout: p.layout || "classic", accent_color: p.accent_color || "", bg_color: p.bg_color || "",
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/pages/${editingId}`, form, { headers: authHeaders() });
        toast.success(t("تم تحديث الصفحة"));
      } else {
        await axios.post(`${API}/admin/pages`, form, { headers: authHeaders() });
        toast.success(t("تم إنشاء الصفحة"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("حذف هذه الصفحة نهائياً؟"))) return;
    await axios.delete(`${API}/admin/pages/${id}`, { headers: authHeaders() });
    toast.success(t("تم الحذف"));
    load();
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`);
    toast.success(t("تم نسخ رابط الصفحة"));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-pages-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl text-luxury-bone">{t("صفحات الهبوط")} ({pages.length})</h2>
        <button
          data-testid="page-add-button"
          onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {t("صفحة جديدة")}
        </button>
      </div>

      {showForm && (
        <form data-testid="page-form" onSubmit={save} className="bg-luxury-surface border border-luxury-border p-8 mb-10 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-luxury-gold">{editingId ? t("تعديل الصفحة") : t("صفحة هبوط جديدة")}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-luxury-gray hover:text-luxury-bone" aria-label={t("إغلاق")}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("الرابط (بالإنجليزية، مثال: healing-workshop) *")}>
              <input data-testid="page-slug-input" dir="ltr" value={form.slug} onChange={set("slug")} className={inputCls} required placeholder="healing-workshop" />
            </Field>
            <Field label={t("نص زر التسجيل")}><input data-testid="page-cta-input" value={form.cta_text} onChange={set("cta_text")} className={inputCls} /></Field>
            <Field label={t("عنوان الصفحة *")}><input data-testid="page-title-input" value={form.title} onChange={set("title")} className={inputCls} required /></Field>
            <Field label={t("العنوان الفرعي")}><input value={form.subtitle} onChange={set("subtitle")} className={inputCls} /></Field>
            <Field label={t("نوع الصفحة")}>
              <select data-testid="page-type-select" value={form.type} onChange={set("type")} className={inputCls}>
                {PAGE_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                ))}
              </select>
            </Field>
            <Field label={t("ربط بدورة (اختياري — يظهر زر لشرائها داخل الصفحة)")}>
              <select data-testid="page-course-select" value={form.course_id} onChange={set("course_id")} className={inputCls}>
                <option value="">{t("— بدون ربط —")}</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </Field>
            <Field label={t("رابط الدفع المباشر (Stripe Payment Link — اختياري)")}>
              <input data-testid="page-payment-link-input" dir="ltr" value={form.payment_link} onChange={set("payment_link")} className={inputCls} placeholder="https://buy.stripe.com/..." />
            </Field>
          </div>
          <Field label={t("نص الصفحة (الوصف)")}><textarea data-testid="page-body-input" value={form.body} onChange={set("body")} rows={4} className={inputCls} /></Field>
          <ImageField testid="page-image-field" label={t("صورة الصفحة (اختياري)")} value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          <ImageField testid="page-bg-field" label={t("خلفية الصفحة (اختياري — تدعم الصور الشفافة PNG)")} value={form.bg_image} onChange={(v) => setForm({ ...form, bg_image: v })} />
          <div className="border border-luxury-gold/30 bg-luxury-gold/5 rounded-xl p-5 space-y-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="page-is-offer" checked={!!form.is_offer} onChange={(e) => setForm({ ...form, is_offer: e.target.checked })} className="w-5 h-5 accent-[#D1A05C]" />
              <span className="text-luxury-gold text-sm font-semibold">{t("عرضها كعرض خاص على الرئيسية")}</span>
            </label>
            {form.is_offer && (
              <>
                <Field label={t("عنوان العرض (اختياري — الافتراضي عنوان الصفحة)")}><input data-testid="page-offer-title" value={form.offer_title} onChange={set("offer_title")} className={inputCls} /></Field>
                <ImageField testid="page-offer-image" label={t("صورة العرض (اختياري — الافتراضي صورة الصفحة)")} value={form.offer_image} onChange={(v) => setForm({ ...form, offer_image: v })} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={t("يبدأ (اختياري)")}><input type="datetime-local" dir="ltr" data-testid="page-offer-start" value={form.offer_start} onChange={set("offer_start")} className={inputCls} /></Field>
                  <Field label={t("ينتهي (اختياري)")}><input type="datetime-local" dir="ltr" data-testid="page-offer-end" value={form.offer_end} onChange={set("offer_end")} className={inputCls} /></Field>
                </div>
                <p className="text-luxury-gray/70 text-xs">{t("يظهر العرض تلقائياً على الرئيسية ضمن الفترة المحددة ويختفي بعدها — بدون حذف الصفحة.")}</p>
              </>
            )}
          </div>
          <div className="border border-luxury-border rounded-xl p-5 space-y-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="page-is-campaign" checked={!!form.is_campaign} onChange={(e) => setForm({ ...form, is_campaign: e.target.checked })} className="w-5 h-5 accent-[#D1A05C]" />
              <span className="text-luxury-bone text-sm font-semibold">{t("صفحة حملة (تُظهر قسم الباقات وطرق الدفع أسفل الصفحة)")}</span>
            </label>
            {form.is_campaign && (
              <>
                <Field label={t("شارة تاريخ البدء (تظهر فوق العنوان)")}><input data-testid="page-start-badge" value={form.start_badge} onChange={set("start_badge")} className={inputCls} placeholder="يبدأ في 20 أكتوبر" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={t("رابط دفع الباقة الأساسية — Regular Package $550")}><input dir="ltr" data-testid="page-pay-regular" value={form.payment_link_regular} onChange={set("payment_link_regular")} className={inputCls} placeholder="https://buy.stripe.com/..." /></Field>
                  <Field label={t("رابط دفع الباقة المميزة — VIP Package $700")}><input dir="ltr" data-testid="page-pay-vip" value={form.payment_link_vip} onChange={set("payment_link_vip")} className={inputCls} placeholder="https://buy.stripe.com/..." /></Field>
                </div>
                <p className="text-luxury-gray/70 text-xs">{t("كل خانة مستقلة — إذا بقيت فارغة يختفي زرها فقط وتبقى الباقة الأخرى تعمل.")}</p>
              </>
            )}
          </div>
          <div className="border border-luxury-gold/30 bg-luxury-gold/5 rounded-xl p-5 space-y-5">
            <p className="text-luxury-gold text-sm font-semibold">{t("مظهر خاص بهذه الصفحة")}</p>
            <Field label={t("تخطيط الصفحة")}>
              <select data-testid="page-layout-select" value={form.layout} onChange={set("layout")} className={inputCls}>
                <option value="classic">{t("كلاسيكي متمركز")}</option>
                <option value="split">{t("صورة جانبية كبيرة")}</option>
                <option value="cinematic">{t("سينمائي بخلفية كاملة (يحتاج صورة خلفية)")}</option>
              </select>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t("لون مميز خاص بالصفحة (اختياري)")}>
                <div className="flex items-center gap-3">
                  <input type="color" data-testid="page-accent-picker" value={form.accent_color || "#D1A05C"} onChange={set("accent_color")} className="w-12 h-12 cursor-pointer bg-transparent border border-luxury-border p-1" />
                  <span className="text-luxury-gray text-xs" dir="ltr">{form.accent_color || "—"}</span>
                  {form.accent_color && <button type="button" data-testid="page-accent-clear" onClick={() => setForm({ ...form, accent_color: "" })} className="text-xs text-luxury-gray hover:text-red-400 transition-colors">{t("مسح")}</button>}
                </div>
              </Field>
              <Field label={t("لون خلفية خاص بالصفحة (اختياري)")}>
                <div className="flex items-center gap-3">
                  <input type="color" data-testid="page-bgcolor-picker" value={form.bg_color || "#1B0E23"} onChange={set("bg_color")} className="w-12 h-12 cursor-pointer bg-transparent border border-luxury-border p-1" />
                  <span className="text-luxury-gray text-xs" dir="ltr">{form.bg_color || "—"}</span>
                  {form.bg_color && <button type="button" data-testid="page-bgcolor-clear" onClick={() => setForm({ ...form, bg_color: "" })} className="text-xs text-luxury-gray hover:text-red-400 transition-colors">{t("مسح")}</button>}
                </div>
              </Field>
            </div>
            <p className="text-luxury-gray text-xs">{t("فارغ = يستخدم ألوان الموقع العامة")}</p>
          </div>
          <FileField testid="page-magnet-field" label={t("الهدية المجانية (Lead Magnet) — تُرسل تلقائياً لكل من يسجل")} value={form.magnet_file_id} onChange={(v) => setForm({ ...form, magnet_file_id: v })} />
          <label className="flex items-center gap-3 text-luxury-bone text-sm cursor-pointer w-fit">
            <input data-testid="page-published-toggle" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 accent-[#D1A05C]" />
            {t("الصفحة منشورة ومتاحة للزوار")}
          </label>
          <div className="border-t border-luxury-border pt-5">
            <p className="text-luxury-gold text-sm mb-4">{t("النسخة الإنجليزية (اختياري)")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Title (EN)"><input dir="ltr" value={form.title_en} onChange={set("title_en")} className={inputCls} /></Field>
              <Field label="Subtitle (EN)"><input dir="ltr" value={form.subtitle_en} onChange={set("subtitle_en")} className={inputCls} /></Field>
            </div>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Body (EN)"><textarea dir="ltr" value={form.body_en} onChange={set("body_en")} rows={3} className={inputCls} /></Field>
              <Field label="Button text (EN)"><input dir="ltr" value={form.cta_text_en} onChange={set("cta_text_en")} className={inputCls} /></Field>
            </div>
          </div>
          <button data-testid="page-save-button" type="submit" disabled={saving} className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editingId ? t("حفظ التعديلات") : t("إنشاء الصفحة")}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {pages.map((p, i) => (
          <div key={p.id} data-testid={`admin-page-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5 flex flex-wrap items-center gap-4 justify-between">
            <div>
              <p className="text-luxury-bone font-semibold text-lg">{p.title}</p>
              <p className="text-luxury-gray text-sm"><span className="text-luxury-gold">{t(typeLabel(p.type))}</span> · <span dir="ltr">/p/{p.slug}</span> {p.is_published === false ? t("— مسودة") : ""} {p.magnet_file_id ? t("— مع هدية") : ""} {p.course_id ? t("— مرتبطة بدورة") : ""}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`admin-page-copy-${i}`} onClick={() => copyLink(p.slug)} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ الرابط")}
              </button>
              <a data-testid={`admin-page-view-${i}`} href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-4 py-2 rounded-full text-sm hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> {t("معاينة")}
              </a>
              <button data-testid={`admin-page-edit-${i}`} onClick={() => startEdit(p)} className="flex items-center gap-2 border border-luxury-border text-luxury-gold px-4 py-2 rounded-full text-sm hover:border-luxury-gold transition-colors">
                <Pencil className="w-4 h-4" strokeWidth={1.5} /> {t("تعديل")}
              </button>
              <button data-testid={`admin-page-delete-${i}`} onClick={() => remove(p.id)} className="flex items-center gap-2 border border-luxury-border text-red-400 px-4 py-2 rounded-full text-sm hover:border-red-400 transition-colors">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("حذف")}
              </button>
            </div>
          </div>
        ))}
        {pages.length === 0 && <p className="text-luxury-gray text-center py-10">{t("لا توجد صفحات بعد — أنشئي أول صفحة هبوط لحدث أو ورشة قادمة")}</p>}
      </div>
    </div>
  );
};
